import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { SCANNER_COMMANDS, buildBarcodeContent } from '../utils/scannerCommands';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import {
  Bot, Send, Loader, ScanBarcode, Printer, Shield,
  Sparkles, User, AlertCircle, ChevronDown, ChevronUp, BookOpen, Trash2, Upload, CheckCircle2
} from 'lucide-react';

function generateBarcodesFromSettings(settings, preferQR = false) {
  if (!settings || typeof settings !== 'object') return [];
  const barcodes = [];

  for (const [key, val] of Object.entries(settings)) {
    if (!val || typeof val !== 'object') continue;
    const operand = parseInt(val.operand, 16) || parseInt(val.operand);
    if (!operand && operand !== 0) continue;

    let data = [];
    let label = key.replace(/_/g, ' ');

    if (Array.isArray(val.value)) {
      data = val.value.map(v => typeof v === 'string' ? parseInt(v, 16) : Number(v));
      label = `${label}: ${val.type || data.map(d => '0x' + d.toString(16).padStart(2, '0')).join(', ')}`;
    } else if (val.value !== undefined) {
      const v = typeof val.value === 'string' ? parseInt(val.value, 16) : Number(val.value);
      data = [v];
      label = `${label}: ${val.mode || val.type || '0x' + v.toString(16).padStart(2, '0').toUpperCase()}`;
    } else continue;

    try {
      const content = buildBarcodeContent(operand, data);
      const cmdEntry = Object.values(SCANNER_COMMANDS).find(c =>
        c.operand === operand && c.multiFormat && c.formats?.includes('QR')
      );
      const format = (preferQR && cmdEntry) ? 'QR' : 'CODE128';
      barcodes.push({ content, label, operand: val.operand, format });
    } catch { /* skip invalid */ }
  }
  return barcodes;
}

async function printBarcodes(title, barcodes) {
  const rendered = await Promise.all(barcodes.map(async bc => {
    if (bc.format === 'QR') {
      const bytes = [];
      for (let i = 0; i < bc.content.length; i++) bytes.push(bc.content.charCodeAt(i));
      const url = await QRCode.toDataURL([{ data: new Uint8Array(bytes), mode: 'byte' }], {
        errorCorrectionLevel: 'M', margin: 2, width: 200,
        color: { dark: '#000000', light: '#ffffff' },
      }).catch(() => null);
      return { svgHtml: url ? `<img src="${url}" style="width:200px;height:200px" />` : '', label: bc.label, operand: bc.operand };
    }
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    try {
      JsBarcode(svg, bc.content, { format: 'CODE128', width: 2, height: 60, displayValue: false, margin: 10, background: '#fff', lineColor: '#000' });
    } catch {}
    return { svgHtml: svg.outerHTML, label: bc.label, operand: bc.operand };
  }));

  const html = rendered.map((item, i) =>
    `<div class="bi"><div class="bn">${i + 1}</div>${item.svgHtml}<p class="bl">${item.label}</p><p class="bs">Op: ${item.operand}</p></div>`
  ).join('');

  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html><html><head><title>SophistiScan AI — ${title}</title>
  <style>body{font-family:monospace;text-align:center;padding:20px}
  .bi{display:block;margin:25px auto;padding-bottom:20px;border-bottom:1px dashed #ccc;page-break-inside:avoid;position:relative}
  .bi:last-child{border-bottom:none}
  .bn{position:absolute;left:10px;top:10px;background:#333;color:#fff;width:24px;height:24px;border-radius:50%;font-size:12px;line-height:24px;text-align:center}
  .bl{font-weight:bold;font-size:13px;margin:5px 0 2px}.bs{color:#666;font-size:11px;margin:0}
  @media print{body{padding:0}.bn{background:#000}}</style></head>
  <body><h2>SophistiScan SQ400H — AI Configuration</h2>
  <p style="color:#666">${title} — Scan in order 1→${rendered.length}</p><hr/>${html}
  <script>setTimeout(()=>window.print(),300)<\/script></body></html>`);
  w.document.close();
}

function QRBarcodeItem({ bc }) {
  const [qrUrl, setQrUrl] = useState(null);
  useEffect(() => {
    const bytes = [];
    for (let i = 0; i < bc.content.length; i++) bytes.push(bc.content.charCodeAt(i));
    QRCode.toDataURL([{ data: new Uint8Array(bytes), mode: 'byte' }], {
      errorCorrectionLevel: 'M', margin: 2, width: 150,
      color: { dark: '#000000', light: '#ffffff' },
    }).then(setQrUrl).catch(() => {});
  }, [bc.content]);
  return (
    <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      {qrUrl && <img src={qrUrl} alt="QR Code" className="mx-auto" style={{ width: 100, height: 100 }} />}
      <p className="text-xs font-medium mt-1 truncate" style={{ color: 'var(--text-primary)' }}>{bc.label}</p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Op: {bc.operand}</p>
    </div>
  );
}

function BarcodePreview({ barcodes }) {
  const canvasRefs = useRef({});

  useEffect(() => {
    barcodes.forEach((bc, i) => {
      if (bc.format === 'QR') return;
      const canvas = canvasRefs.current[i];
      if (!canvas) return;
      try {
        JsBarcode(canvas, bc.content, { format: 'CODE128', width: 1.5, height: 40, displayValue: false, margin: 6, background: 'transparent', lineColor: 'currentColor' });
      } catch {}
    });
  }, [barcodes]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
      {barcodes.map((bc, i) => (
        bc.format === 'QR' ? (
          <QRBarcodeItem key={i} bc={bc} />
        ) : (
          <div key={i} className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <canvas ref={el => canvasRefs.current[i] = el} className="mx-auto max-w-full" style={{ color: 'var(--text-primary)' }} />
            <p className="text-xs font-medium mt-1 truncate" style={{ color: 'var(--text-primary)' }}>{bc.label}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Op: {bc.operand}</p>
          </div>
        )
      ))}
    </div>
  );
}

function ChatMessage({ msg, isHe }) {
  const [showBarcodes, setShowBarcodes] = useState(true);
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === 'user';

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? 'bg-brand-500/20 border border-brand-500/30' : 'bg-purple-500/20 border border-purple-500/30'}`}>
        {isUser ? <User className="w-4 h-4 text-brand-400" /> : <Bot className="w-4 h-4 text-purple-400" />}
      </div>

      {/* Bubble */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${isUser
          ? 'rounded-tr-sm'
          : 'rounded-tl-sm'
        }`}
          style={{
            background: isUser ? 'var(--brand-light)' : 'var(--bg-card)',
            border: `1px solid ${isUser ? 'var(--brand)' : 'var(--border)'}`,
            color: 'var(--text-primary)',
          }}>
          {msg.content}
        </div>

        {/* Settings barcodes */}
        {!isUser && msg.barcodes?.length > 0 && (
          <div className="w-full rounded-xl p-4" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ScanBarcode className="w-4 h-4 text-brand-400" />
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {msg.barcodes.length} {isHe ? 'ברקודי הגדרה' : 'configuration barcodes'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBarcodes(!showBarcodes)}
                  className="btn-ghost text-xs flex items-center gap-1"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {showBarcodes ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showBarcodes ? (isHe ? 'הסתר' : 'Hide') : (isHe ? 'הצג' : 'Show')}
                </button>
                <button
                  onClick={() => printBarcodes(msg.description || 'AI Config', msg.barcodes)}
                  className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
                >
                  <Printer className="w-3.5 h-3.5" />
                  {isHe ? 'הדפס' : 'Print'}
                </button>
              </div>
            </div>
            {msg.description && (
              <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{msg.description}</p>
            )}
            {showBarcodes && <BarcodePreview barcodes={msg.barcodes} />}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiAssistantPage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isHe = i18n.language === 'he';
  const isAdmin = user?.role === 'admin';
  const hasAiAccess = isAdmin || user?.aiAccess;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const knowledgeInputRef = useRef(null);

  const [knowledgeFiles, setKnowledgeFiles] = useState([]); // [{ id, name, chars, uploadedAt }]
  const [uploadingKnowledge, setUploadingKnowledge] = useState(false);
  const [knowledgeMsg, setKnowledgeMsg] = useState('');

  const loadKnowledge = () => {
    api.get('/ai/knowledge').then(r => setKnowledgeFiles(r.data.files || [])).catch(() => {});
  };

  useEffect(() => { loadKnowledge(); }, []);

  const handleKnowledgeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingKnowledge(true);
    setKnowledgeMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/ai/knowledge', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setKnowledgeMsg(isHe ? `✓ נטען בהצלחה — ${(data.chars / 1000).toFixed(0)}K תווים` : `✓ Loaded — ${(data.chars / 1000).toFixed(0)}K chars`);
      loadKnowledge();
    } catch (err) {
      setKnowledgeMsg(isHe ? 'שגיאה: ' + (err.response?.data?.error || err.message) : 'Error: ' + (err.response?.data?.error || err.message));
    } finally { setUploadingKnowledge(false); }
  };

  const handleDeleteKnowledge = async (id, name) => {
    if (!confirm(isHe ? `למחוק את "${name}"?` : `Delete "${name}"?`)) return;
    await api.delete(`/ai/knowledge/${id}`);
    setKnowledgeFiles(prev => prev.filter(f => f.id !== id));
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!hasAiAccess) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {isHe ? 'אין הרשאה' : 'Access Denied'}
        </h2>
      </div>
    );
  }

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const { data } = await api.post('/ai/chat', { message: text, history });

      const wantsQR = /qr/i.test(text);
      const barcodes = data.settings ? generateBarcodesFromSettings(data.settings, wantsQR) : [];

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply,
        barcodes,
        description: data.description,
      }]);
    } catch (err) {
      setError(err.response?.data?.error || (isHe ? 'שגיאה בתקשורת עם ה-AI' : 'AI communication error'));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = isHe ? [
    'הגדר את הסורק למצב CDC (סיריאלי)',
    'הפעל רטט בסריקה מוצלחת',
    'הוסף Enter אחרי כל ברקוד',
    'כבה את הזמזם לחלוטין',
    'הגדר סריקה אוטומטית ללא לחיצה',
  ] : [
    'Set scanner to CDC serial mode',
    'Enable vibration on successful scan',
    'Add Enter after every barcode',
    'Disable all buzzer sounds',
    'Configure auto-sensing scan mode',
  ];

  return (
    <div className="flex flex-col h-full max-h-screen">
      {/* Header */}
      <div className="px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Sparkles className="w-6 h-6 text-purple-400" />
              {isHe ? 'עוזר AI לתכנות סורק' : 'AI Scanner Configuration Assistant'}
            </h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {isHe ? 'תאר מה אתה רוצה שהסורק יעשה — ה-AI יצור עבורך את ברקודי התכנות' : 'Describe what you want the scanner to do — AI will generate the configuration barcodes'}
            </p>
          </div>

          {/* Knowledge base — admin only */}
          {isAdmin && (
            <div className="flex items-center gap-2 shrink-0">
              <input ref={knowledgeInputRef} type="file" accept=".pdf,.txt,.md" onChange={handleKnowledgeUpload} className="hidden" multiple />
              <button onClick={() => knowledgeInputRef.current?.click()} disabled={uploadingKnowledge}
                className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
                style={{ background: '#7c3aed' }}>
                {uploadingKnowledge ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {isHe ? 'הוסף קובץ ידע' : 'Add Knowledge File'}
              </button>
            </div>
          )}
        </div>

        {/* Knowledge file list — admin only */}
        {isAdmin && knowledgeFiles.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {knowledgeFiles.map(f => (
              <div key={f.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e' }}>
                <BookOpen className="w-3 h-3 shrink-0" />
                <span className="max-w-[160px] truncate">{f.name}</span>
                <span style={{ color: 'rgba(34,197,94,0.6)' }}>· {(f.chars / 1000).toFixed(0)}K</span>
                <button onClick={() => handleDeleteKnowledge(f.id, f.name)} className="ml-1 hover:text-red-400 transition-colors" title={isHe ? 'מחק' : 'Delete'}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {isAdmin && knowledgeMsg && (
          <p className="text-xs mt-2" style={{ color: knowledgeMsg.startsWith('✓') ? '#22c55e' : '#f87171' }}>{knowledgeMsg}</p>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Bot className="w-14 h-14 mx-auto mb-4 text-purple-400/50" />
            <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {isHe ? 'שאל אותי כל דבר על תכנות הסורק' : 'Ask me anything about scanner configuration'}
            </p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
              {isHe ? 'לדוגמה:' : 'For example:'}
            </p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(s); inputRef.current?.focus(); }}
                  className="px-3 py-2 rounded-lg text-sm border transition-all hover:border-purple-500/50"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: 'var(--bg-card)' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <ChatMessage key={i} msg={msg} isHe={isHe} />
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-purple-400" />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 rounded-2xl rounded-tl-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <Loader className="w-4 h-4 animate-spin text-purple-400" />
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {isHe ? 'מחשב...' : 'Thinking...'}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-red-400 text-sm bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-4 border-t shrink-0" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
        <div className="flex gap-3 items-end max-w-4xl mx-auto">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isHe ? 'תאר מה אתה רוצה שהסורק יעשה...' : 'Describe what you want the scanner to do...'}
            rows={1}
            className="flex-1 input-field resize-none py-3 min-h-[48px] max-h-[120px]"
            style={{ lineHeight: '1.5' }}
            disabled={loading}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="btn-primary flex items-center justify-center w-12 h-12 rounded-xl shrink-0"
            style={{ background: 'var(--brand)' }}
          >
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>
          {isHe ? 'Enter לשליחה • Shift+Enter לשורה חדשה' : 'Enter to send • Shift+Enter for new line'}
        </p>
      </div>
    </div>
  );
}
